import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { useAuth } from '../lib/auth.jsx';
import { TZ } from '../lib/thTime.js';   // เวลาไทยเสมอ ไม่ตามนาฬิกาเครื่อง

// System-field labels for supplier_column_maps.mapping ({ "<PI header>": "<field>" })
const FIELD_TH = {
  supplier_product_code: 'รหัสซัพพลายเออร์',
  description: 'ชื่อสินค้า',
  qty: 'จำนวน',
  unit_price: 'ราคา/หน่วย',
  amount: 'รวม',
};
const CUR_SYM = { THB: '฿', JPY: '¥', USD: '$', EUR: '€', GBP: '£', CNY: '¥' };
const PO_CHIP = {
  draft: ['ร่าง', 'out'],
  ordering: ['กำลังสั่ง', 'out'],
  ordered: ['กำลังมา', 'amb'],
  arrived: ['ถึงแล้ว', 'amb'],
  completed: ['รับครบ', 'ok'],
  cancelled: ['ยกเลิก', 'out'],
};

const fmtDate = d => d
  ? new Date(d).toLocaleDateString('th-TH-u-ca-gregory', { timeZone: TZ, day: 'numeric', month: 'short', year: 'numeric' })
  : '—';
const fmtNum = n => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

const ArrowSvg = () => (
  <svg viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H9M17 7v8" stroke="#0A0A0A" strokeWidth="1.6"/></svg>
);

function latestMap(s) {
  const maps = s.supplier_column_maps ?? [];
  return maps.length
    ? [...maps].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0]
    : null;
}

function poSummary(po) {
  const items = po.purchase_order_items ?? [];
  const foreign = items.reduce((t, i) => t + Number(i.quantity || 0) * Number(i.unit_price_foreign || 0), 0);
  const thb = items.reduce((t, i) => t + Number(i.quantity || 0) * Number(i.provisional_unit_cost || 0), 0);
  return { count: items.length, foreign, thb };
}

export default function Suppliers() {
  const { hasCostAccess } = useAuth();
  const [rows, setRows] = useState(null);   // null = loading
  const [brands, setBrands] = useState([]);
  const [drawer, setDrawer] = useState(null); // { supplier|null }
  const [loadErr, setLoadErr] = useState('');

  async function load() {
    const [sup, br] = await Promise.all([
      supabase.from('suppliers').select(`
        id, company_name, display_name, currency, contact, is_active, created_at, default_brand_id,
        default_brand:brands!suppliers_default_brand_id_fkey(id, name),
        supplier_brands(brand_id, brand:brands(id, name)),
        supplier_products(supplier_product_code, product_id, product:products(name, sku)),
        supplier_column_maps(id, mapping, is_complete, updated_at),
        purchase_orders(id, po_no, reference, status, currency, created_at,
          purchase_order_items(quantity, unit_price_foreign, provisional_unit_cost))
      `).eq('is_active', true).order('company_name'),
      supabase.from('brands').select('id, name').order('name'),
    ]);
    if (sup.error) setLoadErr(sup.error.message);
    setRows(sup.data ?? []);
    setBrands(br.data ?? []);
  }
  useEffect(() => { load(); }, []);

  return (
    <>
      <div className="hero">
        <div className="eyebrow">ซัพพลายเออร์ · Suppliers</div>
        <h1>ใครส่งของ<br />แบรนด์อะไร</h1>
        <p className="sub">
          ระบบจำไว้ว่าซัพพลายเออร์แต่ละรายคือแบรนด์อะไร (บ่อยครั้งชื่อบริษัทกับแบรนด์คนละชื่อ)
          จำรูปแบบคอลัมน์ในไฟล์ PI และจำรหัสสินค้าของซัพพลายเออร์ที่จับคู่กับสินค้าในระบบแล้ว —
          ครั้งต่อไปนำเข้าได้เร็วขึ้น
        </p>
      </div>

      <div className="sup-toolbar">
        <span className="sup-count">{rows ? `${rows.length} ราย` : 'กำลังโหลด…'}</span>
        <button className="add-sup" onClick={() => setDrawer({ supplier: null })} disabled={!hasCostAccess}
                title={hasCostAccess ? '' : 'เฉพาะ role จัดซื้อ / ผู้จัดการ'}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="#0A0A0A" strokeWidth="1.7"/></svg>
          เพิ่มซัพพลายเออร์
        </button>
      </div>

      <div className="sup-head">
        <span className="first">ซัพพลายเออร์ · Supplier</span>
        <span>สินค้า</span>
        <span>รหัสจำแล้ว</span>
        <span className="lay">Column layout</span>
        <span></span>
      </div>

      <div className="sup-list">
        {loadErr && <p className="err-note">โหลดไม่สำเร็จ: {loadErr}</p>}
        {rows && rows.length === 0 && !loadErr && (
          <div className="list-empty">
            ยังไม่มีซัพพลายเออร์ — กด "เพิ่มซัพพลายเออร์" เพื่อเริ่ม หรือระบบจะสร้างให้ตอนนำเข้า PI ครั้งแรก
          </div>
        )}
        {(rows ?? []).map(s => {
          const codes = s.supplier_products ?? [];
          const products = new Set(codes.map(c => c.product_id)).size;
          const map = latestMap(s);
          const lastPo = (s.purchase_orders ?? [])
            .map(p => p.created_at).sort().at(-1);
          return (
            <div className="sup-card" key={s.id} onClick={() => setDrawer({ supplier: s })}>
              <div className="sup-left">
                <div className="sup-co">{s.company_name}</div>
                <div className="sup-map">
                  <span className="mini">แบรนด์</span>
                  <span className="tag solid">{s.default_brand?.name ?? '—'}</span>
                  <span className="tag">{s.currency}</span>
                  <span className="last">ล่าสุด {fmtDate(lastPo)}</span>
                </div>
              </div>
              <div className="sup-num">{products}</div>
              <div className="sup-num">{codes.length}</div>
              <div className="sup-lay">
                {map?.is_complete
                  ? <span className="tag">layout ✓</span>
                  : map
                    ? <span className="chip-warn">column ยังไม่ครบ</span>
                    : <span className="sd-empty">ยังไม่เคยนำเข้า</span>}
              </div>
              <span className="arrow sm"><ArrowSvg /></span>
            </div>
          );
        })}
      </div>

      {drawer && (
        <SupplierDrawer
          supplier={drawer.supplier}
          brands={brands}
          canWrite={hasCostAccess}
          onClose={() => setDrawer(null)}
          onSaved={() => { setDrawer(null); load(); }}
        />
      )}
    </>
  );
}

function SupplierDrawer({ supplier, brands, canWrite, onClose, onSaved }) {
  const isNew = !supplier;
  const [co, setCo] = useState(supplier?.company_name ?? '');
  const [cur, setCur] = useState(supplier?.currency ?? 'JPY');
  const [brandName, setBrandName] = useState(supplier?.default_brand?.name ?? '');
  const [email, setEmail] = useState(supplier?.contact?.email ?? '');
  const [phone, setPhone] = useState(supplier?.contact?.phone ?? '');
  const [address, setAddress] = useState(supplier?.contact?.address ?? '');
  const bank0 = supplier?.contact?.bank ?? {};
  const [bankName, setBankName] = useState(bank0.bank_name ?? '');
  const [accountNo, setAccountNo] = useState(bank0.account_no ?? '');
  const [swift, setSwift] = useState(bank0.swift ?? '');
  const [beneficiary, setBeneficiary] = useState(bank0.beneficiary ?? '');
  const [bankAddress, setBankAddress] = useState(bank0.bank_address ?? '');
  const [extras, setExtras] = useState((supplier?.supplier_brands ?? []).map(b => b.brand?.name).filter(Boolean));
  const [extraInput, setExtraInput] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const map = supplier ? latestMap(supplier) : null;
  const codes = supplier?.supplier_products ?? [];
  const pos = useMemo(
    () => [...(supplier?.purchase_orders ?? [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [supplier]
  );

  async function resolveBrandId(name) {
    const trimmed = name.trim();
    if (!trimmed) return null;
    const hit = brands.find(b => b.name.toLowerCase() === trimmed.toLowerCase());
    if (hit) return hit.id;
    const { data, error } = await supabase.from('brands').insert({ name: trimmed }).select('id').single();
    if (error) throw new Error(`สร้างแบรนด์ "${trimmed}" ไม่สำเร็จ: ${error.message}`);
    return data.id;
  }

  async function save() {
    if (busy || !canWrite) return;
    if (!co.trim()) { setErr('กรอกชื่อบริษัทก่อน'); return; }
    setBusy(true); setErr('');
    try {
      const defaultBrandId = await resolveBrandId(brandName);
      const row = {
        company_name: co.trim(),
        currency: (cur.trim() || 'THB').toUpperCase(),
        default_brand_id: defaultBrandId,
        contact: {
          ...(supplier?.contact ?? {}),
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          address: address.trim() || undefined,
          bank: {
            bank_name: bankName.trim() || undefined,
            account_no: accountNo.trim() || undefined,
            swift: swift.trim() || undefined,
            beneficiary: beneficiary.trim() || undefined,
            bank_address: bankAddress.trim() || undefined,
          },
        },
        updated_at: new Date().toISOString(),
      };
      let supplierId = supplier?.id;
      if (isNew) {
        const { data, error } = await supabase.from('suppliers').insert(row).select('id').single();
        if (error) throw error;
        supplierId = data.id;
      } else {
        const { error } = await supabase.from('suppliers').update(row).eq('id', supplierId);
        if (error) throw error;
      }
      // sync extra brands (supplier_brands)
      const wantIds = [];
      for (const name of extras) wantIds.push(await resolveBrandId(name));
      const haveIds = (supplier?.supplier_brands ?? []).map(b => b.brand_id);
      const add = wantIds.filter(id => id && !haveIds.includes(id));
      const del = haveIds.filter(id => !wantIds.includes(id));
      if (add.length) {
        const { error } = await supabase.from('supplier_brands')
          .insert(add.map(brand_id => ({ supplier_id: supplierId, brand_id })));
        if (error) throw error;
      }
      if (del.length) {
        const { error } = await supabase.from('supplier_brands')
          .delete().eq('supplier_id', supplierId).in('brand_id', del);
        if (error) throw error;
      }
      onSaved();
    } catch (e) {
      setErr(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  function addExtra() {
    const v = extraInput.trim();
    if (v && !extras.some(x => x.toLowerCase() === v.toLowerCase())) setExtras([...extras, v]);
    setExtraInput('');
  }

  return (
    <>
      <div className="scrim open" onClick={onClose} />
      <aside className="drawer open" aria-hidden="false">
        <div className="drawer-head">
          <div>
            <div className="eyebrow">{isNew ? 'เพิ่มซัพพลายเออร์ · New supplier' : 'ซัพพลายเออร์ · Supplier'}</div>
            <h2>{isNew ? 'เพิ่มซัพพลายเออร์' : supplier.company_name}</h2>
          </div>
          <button className="xbtn" onClick={onClose} aria-label="ปิด">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="#0A0A0A" strokeWidth="1.6"/></svg>
          </button>
        </div>

        <div className="drawer-body">
          <div className="fx-row" style={{ marginBottom: 6 }}>
            <div className="field">
              <div className="lbl"><span className="th">ชื่อบริษัท</span><span className="en">Company</span></div>
              <input className="under" value={co} onChange={e => setCo(e.target.value)}
                     placeholder="ชื่อบริษัท / supplier" disabled={!canWrite} />
            </div>
            <div className="field">
              <div className="lbl"><span className="th">สกุลเงิน</span><span className="en">Currency</span></div>
              <input className="under mono" value={cur} onChange={e => setCur(e.target.value)} disabled={!canWrite} />
            </div>
          </div>
          <div className="field">
            <div className="lbl"><span className="th">แบรนด์ (ค่าเริ่มต้น)</span><span className="en">Default brand</span></div>
            <input className="under" value={brandName} onChange={e => setBrandName(e.target.value)}
                   placeholder="เช่น Nalgene" list="brand-options" disabled={!canWrite} />
            <datalist id="brand-options">
              {brands.map(b => <option key={b.id} value={b.name} />)}
            </datalist>
            <div className="brand-chips">
              {extras.map(name => (
                <span className="brand-chip" key={name}>
                  {name}
                  {canWrite && <button onClick={() => setExtras(extras.filter(x => x !== name))} aria-label={`ลบ ${name}`}>×</button>}
                </span>
              ))}
              {canWrite && (
                <input className="under" style={{ width: 150, fontSize: 13 }} value={extraInput}
                       placeholder="+ แบรนด์เพิ่มเติม ↵"
                       onChange={e => setExtraInput(e.target.value)}
                       onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addExtra(); } }} />
              )}
            </div>
          </div>
          <div className="cost-note" style={{ borderColor: 'var(--ink)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flex: 'none', marginTop: 1 }}><circle cx="12" cy="12" r="9" stroke="#6E6E6E" strokeWidth="1.4"/><path d="M12 11v5M12 8h.01" stroke="#6E6E6E" strokeWidth="1.6"/></svg>
            <span>ชื่อบริษัทกับแบรนด์เป็นคนละชื่อได้ · 1 ซัพพลายเออร์ตั้งได้หลายแบรนด์ แต่ default ไว้ 1</span>
          </div>

          <div className="fx-row">
            <div className="field">
              <div className="lbl"><span className="th">อีเมล</span><span className="opt">ไม่บังคับ</span></div>
              <input className="under" value={email} onChange={e => setEmail(e.target.value)} disabled={!canWrite} />
            </div>
            <div className="field">
              <div className="lbl"><span className="th">โทร</span><span className="opt">ไม่บังคับ</span></div>
              <input className="under mono" value={phone} onChange={e => setPhone(e.target.value)} disabled={!canWrite} />
            </div>
          </div>
          <div className="field">
            <div className="lbl"><span className="th">ที่อยู่</span><span className="en">Address</span><span className="opt">ไม่บังคับ</span></div>
            <textarea className="under" rows={2} value={address} onChange={e => setAddress(e.target.value)}
                      disabled={!canWrite} style={{ resize: 'vertical', lineHeight: 1.5, fontSize: 15 }} />
          </div>

          <div className="sd-sec">
            <h5>การชำระเงิน · Payment / remittance</h5>
            <div className="fx-row">
              <div className="field">
                <div className="lbl"><span className="th">ธนาคาร</span><span className="en">Bank</span></div>
                <input className="under" value={bankName} onChange={e => setBankName(e.target.value)} disabled={!canWrite} />
              </div>
              <div className="field">
                <div className="lbl"><span className="th">เลขที่บัญชี</span><span className="en">Account no.</span></div>
                <input className="under mono" value={accountNo} onChange={e => setAccountNo(e.target.value)} disabled={!canWrite} />
              </div>
            </div>
            <div className="fx-row">
              <div className="field">
                <div className="lbl"><span className="th">SWIFT code</span></div>
                <input className="under mono" value={swift} onChange={e => setSwift(e.target.value.toUpperCase())} disabled={!canWrite} />
              </div>
              <div className="field">
                <div className="lbl"><span className="th">ชื่อบัญชี</span><span className="en">Beneficiary</span></div>
                <input className="under" value={beneficiary} onChange={e => setBeneficiary(e.target.value)} disabled={!canWrite} />
              </div>
            </div>
            <div className="field">
              <div className="lbl"><span className="th">ที่อยู่ธนาคาร</span><span className="en">Bank address</span><span className="opt">ใช้ตอนโอนต่างประเทศ</span></div>
              <textarea className="under" rows={2} value={bankAddress} onChange={e => setBankAddress(e.target.value)}
                        disabled={!canWrite} style={{ resize: 'vertical', lineHeight: 1.5, fontSize: 15 }} />
            </div>
          </div>

          {!isNew && map && !map.is_complete && (
            <div className="cost-note" style={{ marginTop: 2 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flex: 'none', marginTop: 1 }}><path d="M12 3l9 16H3z" stroke="#BF7A15" strokeWidth="1.4"/><path d="M12 10v4M12 16h.01" stroke="#BF7A15" strokeWidth="1.6"/></svg>
              <span>ตั้งค่าคอลัมน์ยังไม่ครบ — จับคู่คอลัมน์ "จำนวน" ก่อนนำเข้า PI ครั้งถัดไป</span>
            </div>
          )}

          {!isNew && pos.length > 0 && (
            <div className="sd-sec">
              <h5>ประวัติการสั่งซื้อ ({pos.length})</h5>
              {pos.map(p => {
                const sum = poSummary(p);
                const chip = PO_CHIP[p.status] ?? [p.status, 'out'];
                const sym = CUR_SYM[p.currency] ?? '';
                return (
                  <div className="po-row" key={p.id}>
                    <div>
                      <div className="po-ref">{p.reference || p.po_no}</div>
                      <div className="po-date">{fmtDate(p.created_at)} · {sum.count} รายการ</div>
                    </div>
                    <div>
                      <div className="po-amt">{sym}{fmtNum(sum.foreign)}</div>
                      <div className="po-thb">≈ ฿{fmtNum(sum.thb)}</div>
                    </div>
                    <div className={`po-chip ${chip[1]}`}>{chip[0]}</div>
                  </div>
                );
              })}
            </div>
          )}

          {!isNew && (
            <>
              <div className="sd-sec">
                <h5>รูปแบบคอลัมน์ที่จำไว้ · Column layout</h5>
                {map && Object.keys(map.mapping ?? {}).length
                  ? Object.entries(map.mapping).map(([header, field]) => (
                      <div className="map-row" key={header}>
                        <span className="pi">{header}</span><span className="arw">→</span>
                        <span className="sys">{FIELD_TH[field] ?? field}</span>
                      </div>
                    ))
                  : <p className="sd-empty">ยังไม่มี — ระบบจะจำรูปแบบคอลัมน์อัตโนมัติหลังนำเข้า PI ครั้งแรก</p>}
              </div>
              <div className="sd-sec">
                <h5>รหัสสินค้าที่จับคู่แล้ว · Remembered codes {codes.length ? `(${codes.length})` : ''}</h5>
                {codes.length
                  ? <>
                      {codes.slice(0, 3).map(c => (
                        <div className="code-row" key={c.supplier_product_code}>
                          <span className="pi">{c.supplier_product_code}</span><span className="arw">→</span>
                          <span className="sys"><b>{c.product?.sku ?? '—'}</b> · {c.product?.name ?? '—'}</span>
                        </div>
                      ))}
                      {codes.length > 3 && <div className="code-more">และอีก {codes.length - 3} รหัส</div>}
                    </>
                  : <p className="sd-empty">ยังไม่มีรหัสที่จับคู่</p>}
              </div>
            </>
          )}

          <p className="err-note">{err}</p>
        </div>

        <div className="drawer-foot">
          <span className="txt">{canWrite ? 'บันทึกซัพพลายเออร์' : 'ดูอย่างเดียว'}</span>
          <button className="arrow" onClick={canWrite ? save : onClose} disabled={busy} aria-label="บันทึก">
            <ArrowSvg />
          </button>
        </div>
      </aside>
    </>
  );
}
